import type { ConsoleTab } from '@badger-sentinel/rules';

import { DATA_TAG } from '../../theme/type.js';
import { tabLabel } from '../model/tabs.js';

/**
 * The fallback centre pane (design 1138–1142).
 *
 * It names the tab it is standing in for rather than apologising for itself — a
 * caption explaining that a module is coming later is the console explaining
 * itself to itself.
 *
 * ## It fills the pane now
 *
 * It was `flex: 'none'` with `minHeight: 120` inside a `flex: 1` scroller, so on
 * the GM console the instrument brackets framed a 120px box floating at the top
 * of an otherwise blank 570px canvas — the signature geometry drawing attention
 * to a hole. `flex: 1` makes the brackets frame the pane, which is what the
 * Bracket Rule says they are for: once per screen, on the canvas.
 *
 * ## `NO MODULE` no longer means two different things
 *
 * The same string covered "your snapshot has not arrived yet" and "this was
 * never built", so a seat waiting two seconds for its first view read the same
 * words as a seat looking at a deferred feature, and both read as a fault.
 * `reason` splits them: a tab that exists but has no data says `NO PICTURE`,
 * matching what the rails already say for the same condition, and a tab with no
 * module behind it says so.
 */

export type OtherViewReason = 'NO_MODULE' | 'NO_PICTURE';

export function OtherView(props: {
  tab: ConsoleTab | null;
  reason?: OtherViewReason;
}): JSX.Element {
  const reason = props.reason ?? 'NO_MODULE';
  const word = reason === 'NO_PICTURE' ? 'NO PICTURE' : 'NO MODULE';

  return (
    <div
      data-testid="other-view"
      data-reason={reason}
      data-mat="ticks"
      style={{
        flex: 1,
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--canvas)',
      }}
    >
      <span style={{ ...DATA_TAG, fontSize: 11, color: 'var(--text-muted)' }}>
        {props.tab === null ? word : `${tabLabel(props.tab)} — ${word}`}
      </span>
    </div>
  );
}
